import React, { FC, KeyboardEvent, Ref, SyntheticEvent } from 'react';
import { CommonFieldProps, InputProps, StylableComponentProps } from '@cld/common-definitions';
import { ToggleBase } from './Toggle.styled';

export interface ToggleProps extends CommonFieldProps<boolean, SyntheticEvent<HTMLInputElement>>, StylableComponentProps {
  /** Id of the HTML input. */
  id?: string;
  /** Ref forwarded to HTML input element inside. */
  inputRef?: Ref<HTMLInputElement>;
  /** Props passed directly to HTML input inside **(overriding props already handled by abstraction)**. */
  inputProps?: InputProps;

  // TODO: move it to some shared types, it shouldn't be available only for Toggle fields
  // Accessibility attributes
  tabIndex?: number;
  title?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/**
 * Use `Toggle` to control binary state in a more eye-pleasing way than checkbox
 */
export const Toggle: FC<ToggleProps> = ({
  name,
  id,
  inputRef,
  value,
  defaultValue,
  onChange,
  onClick,
  onFocus,
  onBlur,
  isDisabled = false,
  validationState = 'none',
  inputProps,
  ...restProps
}) => {
  const handleOnChange = (event: SyntheticEvent<HTMLInputElement>) => onChange?.(event.currentTarget.checked, event);
  const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isDisabled && event.key === 'Enter') {
      handleOnChange(event);
    }
  };

  return (
    <ToggleBase
      {...inputProps}
      name={name}
      id={id}
      type="checkbox"
      role="switch"
      value={String(value)}
      checked={value}
      defaultChecked={defaultValue}
      aria-checked={value}
      disabled={isDisabled}
      onChange={handleOnChange}
      onKeyPress={onKeyPress}
      validationState={validationState}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      ref={inputRef}
      {...restProps}
    />
  );
};
